import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import axios from "../../api/axios";
import { formatDataGeneral } from "../../utils/utils";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

const Table_10 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);
  const [showChart, setShowChart] = useState(false);

  useEffect(() => {
    getData();
  }, [year, district, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_10",
      tableData
    }),
  }));

  const mapData = (data, report) => {
    const names = [
      "COMPENSATION", "GOODS AND SERVICES", "INVESTMENTS/ASSETS"
    ];

    const attributes = data[0]?.attributes;
    const reports = report.find(rep => rep.trackedEntity === data[0]?.trackedEntity);

    const result = names.map(name => {
      const releasedItem = attributes?.find(item =>
        item?.displayName?.toLowerCase() === `${name.toLowerCase()} released`
      );
      const targetItem = attributes?.find(item =>
        item?.displayName?.toLowerCase() === `${name.toLowerCase()} target`
      );

      return {
        name: name,
        released: releasedItem ? Number(releasedItem.value) : 0,
        target: targetItem ? Number(targetItem.value) : 0,
        actual: 0
      };
    });

    result.forEach(el => {
      for (let r of reports?.dataValues || []) {
        if (el.name === 'COMPENSATION' && r.dataElement === "iF3bVYzJUE6") {
          el.actual = Number(r.value);
          break;
        } else if (el.name === 'GOODS AND SERVICES' && r.dataElement === "ZKwpRsX6DIE") {
          el.actual = Number(r.value);
          break;
        } else if (el.name === 'INVESTMENTS/ASSETS' && r.dataElement === "LKTrRHSCoEk") {
          el.actual = Number(r.value);
          break;
        }
      }
    });

    return result;
  };

  function transformDisbursements(data) {
    const keys = [
      "disbursementsThreeLessMapped",
      "disbursementsTwoLessMapped",
      "disbursementsOneLessMapped",
      "disbursementsFirstMapped"
    ];

    const suffixes = ["One", "Two", "Three", "Four"];

    // Get all names from the first set
    const names = data[keys[0]].map(item => item.name);

    // Transform grouped data
    const result = names.map(name => {
      let obj = { name };

      keys.forEach((key, i) => {
        const match = data[key].find(item => item.name === name);
        if (match) {
          obj[`released${suffixes[i]}`] = match.released;
          obj[`target${suffixes[i]}`] = match.target;
          obj[`actual${suffixes[i]}`] = match.actual;
        }
      });

      return obj;
    });

    // Build totals row
    const totalRow = { name: "Total" };

    suffixes.forEach((suf, i) => {
      totalRow[`released${suf}`] = result.reduce(
        (sum, el) => sum + (el[`released${suf}`] || 0),
        0
      );
      totalRow[`target${suf}`] = result.reduce(
        (sum, el) => sum + (el[`target${suf}`] || 0),
        0
      );
      totalRow[`actual${suf}`] = result.reduce(
        (sum, el) => sum + (el[`actual${suf}`] || 0),
        0
      );
    });

    result.push(totalRow);
    return result;
  }



  function getData() {
    if (!district) {
      setTableData([]);
      return;
    }

    const startDate = `${year - 3}-01-01`;
    const endDate = `${year}-12-31`;

    axios
      .get(`/tracker/trackedEntities?orgUnit=${district}&program=WHILilRZRhT&startDate=${startDate}&endDate=${endDate}&pageSize=5000`)
      .then(result => {
        if (result.data.instances.length > 0) {
          axios
            .get(`/tracker/events?program=WHILilRZRhT&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`)
            .then(resp => {

              const years = [`${year - 3}`, `${year - 2}`, `${year - 1}`, `${year}`]
              const disbursementsFirst = formatDataGeneral(result.data.instances, "Years", `${year}`) || [];
              const disbursementsOneLess = formatDataGeneral(result.data.instances, "Years", `${year - 1}`) || [];
              const disbursementsTwoLess = formatDataGeneral(result.data.instances, "Years", `${year - 2}`) || [];
              const disbursementsThreeLess = formatDataGeneral(result.data.instances, "Years", `${year - 3}`) || [];
              const reports = resp.data.instances;

              const reportFiltered = reports.filter(rep => rep.programStage === "yJ86MwzF5Ak")

              const disbursementsFirstMapped = mapData(disbursementsFirst, reportFiltered);
              const disbursementsOneLessMapped = mapData(disbursementsOneLess, reportFiltered);
              const disbursementsTwoLessMapped = mapData(disbursementsTwoLess, reportFiltered);
              const disbursementsThreeLessMapped = mapData(disbursementsThreeLess, reportFiltered);
              const tempResult = { disbursementsFirstMapped, disbursementsOneLessMapped, disbursementsTwoLessMapped, disbursementsThreeLessMapped };
              const formatedData = transformDisbursements(tempResult);

              setTableData(formatedData);
            })
            .catch(err => console.log(err));
        } else {
          setTableData([]);
        }
      })
      .catch(err => console.log(err));
  }

  const chartYears = [year - 3, year - 2, year - 1, Number(year)];
  const yearSuffixes = ["One", "Two", "Three", "Four"];
  const totals = tableData.find((item) => item.name === "Total");

  const getTotalSeries = (prefix) => yearSuffixes.map(
    (suffix) => Number(totals?.[`${prefix}${suffix}`]) || 0,
  );

  const chartData = {
    labels: chartYears,
    datasets: [
      {
        label: "Approved",
        data: getTotalSeries("target"),
        borderColor: "#0d6efd",
        backgroundColor: "rgba(13, 110, 253, 0.15)",
        pointBackgroundColor: "#0d6efd",
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0.25,
      },
      {
        label: "Released",
        data: getTotalSeries("released"),
        borderColor: "#fd7e14",
        backgroundColor: "rgba(253, 126, 20, 0.15)",
        pointBackgroundColor: "#fd7e14",
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0.25,
      },
      {
        label: "Expenditure",
        data: getTotalSeries("actual"),
        borderColor: "#198754",
        backgroundColor: "rgba(25, 135, 84, 0.15)",
        pointBackgroundColor: "#198754",
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0.25,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Figure 2.4 – Approved, Released and Expenditure Trends (${year - 3}–${year})`,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${new Intl.NumberFormat("en-GH", {
            style: "currency",
            currency: "GHS",
            minimumFractionDigits: 2,
          }).format(context.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: "Expenditure in GH¢",
        },
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return new Intl.NumberFormat("en-GH", {
              style: "currency",
              currency: "GHS",
              minimumFractionDigits: 0,
            }).format(value);
          },
        },
      },
      x: {
        title: {
          display: true,
          text: "Financial Year",
        },
      },
    },
  };

  return (
    <div className="col-12">
      <h3>Table 10 - Update on Expenditure as of the Year ({year - 3} – {year})</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          {/* <h5>2.2.3 Update on Disbursements</h5> */}
          <h7>
            During the year under review, funds received were disbursed under the components of
            Compensation, Goods and Services and Non–Financial Assets. Table 2.4 presents the
            disbursement for the years.</h7>
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table10-${year}`}
            hideTableDis={hideTableDis}

          />
          {/* {JSON.stringify(tableData)} */}
          <div className="table-responsive">
            <table className="table table-bordered"
              style={{
                tableLayout: "fixed",
                border: '1px solid #000',
                borderCollapse: 'collapse',
                width: '100%',
                marginTop: "20px"
              }}>
              <thead style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}
              >
                <tr>
                  <th style={{ border: "1px solid #000" }} rowSpan="2">Budget Items</th>
                  {[`${year - 3}`, `${year - 2}`, `${year - 1}`, `${year}`].map(year => (
                    <th key={year} style={{ border: "1px solid #000" }} colSpan="3">
                      {year}
                    </th>
                  ))}
                </tr>
                <tr>
                  {[`${year - 3}`, `${year - 2}`, `${year - 1}`, `${year}`].flatMap(year => [
                    <th key={`${year}-approved`} style={{ border: "1px solid #000" }}>Approved</th>,
                    <th key={`${year}-released`} style={{ border: "1px solid #000" }}>Released</th>,
                    <th key={`${year}-expenditure`} style={{ border: "1px solid #000" }}>Expenditure</th>,
                  ])}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={index}>
                    <td style={{ border: "1px solid #000" }}>{row.name !== 'INVESTMENTS/ASSETS' ? row.name : 'CAPEX '}</td>

                    <td style={{ border: "1px solid #000" }}>
                      {row.targetOne.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.releasedOne.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.actualOne.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.targetTwo.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.releasedTwo.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.actualTwo.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.targetThree.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.releasedThree.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.actualThree.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.targetFour.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.releasedFour.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ border: "1px solid #000" }}>
                      {row.actualFour.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>


                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            className="btn btn-primary mt-3"
            onClick={() => setShowChart(!showChart)}
          >
            {showChart ? "Hide Figure 2.4" : "Show Figure 2.4"}
          </button>
          {showChart && (
            <div className="mt-4" style={{ height: "400px" }}>
              {totals ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <p className="text-center">No expenditure data available for this period.</p>
              )}
            </div>

          )}

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table10-${year}`}
              hideTableDis={hideTableDis}

          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_10;